import { TransactionType } from './invoices';
import { LubricantTypeEnums } from './port-statement';
import { VesselType } from './vessel-types';

export enum InventoryItemType {
  STORE = 'store',
  SPARE_PART = 'spare_part'
}

export enum PurchasingType {
  LUBRICANTS = 'lubricants',
  PAINTS = 'paints',
  STORE = 'purchasing',
  CHEMICALS = 'chemicals'
}

export enum OwnershipType {
  OWNED = 'owned',
  BAREBOAT = 'bareboat',
  TC_PERIOD = 'tc_period',
  TC_TRIP = 'tc_trip'
}

export enum CveType {
  PER_30 = 'per_30',
  PER_MONTH = 'per_month',
  LUMP_SUM = 'lump_sum'
}

export interface OwnershipTypeOptionType {
  label: string;
  value: OwnershipType;
}

export type CveTypeOptionType = {
  id: CveType;
  label: string;
  default?: boolean;
};

export interface Country {
  id: number;
  code: string;
  name: string;
  created_at: string;
  updated_at: string;
  updated_by_organization: boolean;
  hidden: boolean;
  hidden_by_default: boolean;
  is_european_union: boolean;
  updated_at_on_sync_to_onboard: string;
  deleted_at: string | null;
}

export interface Nationality {
  country_id: number;
  country?: Country;
  id: number;
  name: string;
}

export interface Status {
  id: number;
  label: string;
  name: string;
  color: string;
  edit_side?: StatusEditSide;
  progress?: StatusProgress;
  is_for_vessel: boolean;
  is_for_office: boolean;
}

export interface StaticStatus {
  color: string;
  name: string;
  value: string;
}

export type StatusProgress =
  | 'not_started'
  | 'working_on'
  | 'waiting'
  | 'for_approval'
  | 'cancelled'
  | 'done'
  | 'failed'
  | 'for_corrections';

export type StatusEditSide = 'office' | 'vessel';

export interface Rank {
  id: number;
  name: string;
  abbreviation: string | null;
  sort_index: number;
}

export interface CrewRank extends Rank {
  created_at: string;
  updated_at: string;
  department: string;
  hidden: boolean;
  hidden_by_default: boolean;
  top_4: boolean;
  is_officer: boolean;
  permission_template_id: number;
  updated_at_on_sync_to_onboard: string;
  deleted_at: string | null;
}

export interface SignOffReason {
  id: number;
  name: string;
}

export type ImportanceLabelType = 'vlow' | 'low' | 'normal' | 'high' | 'vhigh' | 'critical';

export interface Importance {
  id: number;
  label: ImportanceLabelType;
  name: string;
}

export interface CurrencyForexRate {
  base: string;
  currency_label: string;
  last_update: string;
  rate: string;
}

export interface Currency {
  id: number;
  label: string;
  symbol?: string;
  name: string;
}

export interface CompanyType {
  id: number;
  name: string;
}

export interface Department {
  id: number;
  name: string;
  label: string;
  color: string;
  is_crew_department?: boolean;
}

export interface DepartmentRole {
  id: number;
  name: string;
  is_protected?: boolean;
}

export interface ScheduleIntervals {
  label: string;
  name: string;
}
export interface ScaleLabel {
  color: string;
  label: string;
  name: string;
}
export interface BudgetScenarioType {
  id: number;
  code: string;
  name: string;
}
export interface Scale {
  max: number;
  min: number;
  step: number;
  label: ScaleLabel[];
}

export type CriteriaTypeLabel = 'free-text' | 'question' | 'scale' | 'skippable-question';

export interface CriteriaType {
  label: CriteriaTypeLabel;
  name: string;
}

export interface BankAccount {
  id: number;
  iban: string;
  swift_code: string;
  bank_name: string;
  account_no: string;
  currency: Currency | null;
}

export interface RiskCategory {
  id: number;
  name: string;
}

export interface RiskAssessment {
  id: string;
  name: string;
}

export type FuelGradeCategoryLabel =
  | 'fo'
  | 'do'
  | 'go'
  | 'biofuel'
  | 'lng'
  | 'lpg_butane'
  | 'lpg_propane'
  | 'ethanol'
  | 'methanol';

export interface FuelGrade {
  id: number;
  label: string;
  name: string;
  category: FuelGradeCategoryLabel;
}

export interface FuelGradeViscosities {
  fuel_grade_id: number;
  values: string[];
}

export type LabeValueListingOptionType = {
  label: string;
  value: string;
};

export type LabelNameListingOptionType = {
  label: string;
  name: string;
};

export type IdNameListingOptionType = {
  id: number;
  name: string;
};

export type IdLabelListingOptionType = {
  id: number | string;
  label: string;
};

export type IdNameLabelListingOptionType = {
  id: number;
  name: string;
  label: string;
};

export type ClassNumbersType = {
  name: string;
  label: string;
};

export type IdNameValueListingOptionType = {
  name: string;
  value: string;
};

export type LubricantOptionType = {
  name: string;
  value: LubricantTypeEnums;
};

export type InvoiceCompanyTypes = {
  name: string;
  value: TransactionType;
};

export interface KeyLabelListingOptionType {
  key: string;
  label: string;
}

export type CreatableEmailType = {
  id: string;
  full_name: string;
};

export type CrewProfileDefaultViewType =
  | 'personal_main_info'
  | 'contracts_detailed'
  | 'contracts_list';

export type EventTemplateListingOptionType = {
  id: number;
  template_name: string;
  name: string;
};

export type EventAccountCategoryType = { name: string; label: 'expense' | 'income' };

export interface NormalBalanceType {
  name: string;
  value: string;
}

export enum TagTypeEnum {
  FORWARDING_CASE = 'forwarding_case',
  PURCHASING_REQUISITION = 'purchasing_requisition',
  PURCHASING_REQUISITION_ITEM_COMMENT = 'purchasing_requisition_item_comment',
  COMPANY_CONNECTION_TYPE = 'company_connection_type',
  EVENT = 'event',
  EVENT_OFFICE_NOTE = 'event_office_note',
  JOB = 'job',
  POST = 'post',
  FORM_FIELD = 'form_field',
  NOTE = 'note',
  DRILL = 'drill',
  CREW_TRAINING_TYPE = 'crew_training_type',
  STORE = 'store',
  INVOICE = 'invoice',
  FINDING = 'finding'
}

export interface CrewShiftType {
  id: number;
  name: string;
  color: string;
}

export enum ContactTypeEnum {
  EMAIL = 'email',
  PHONE = 'phone'
}

export enum PaymentTermLabelEnum {
  ThirtyDays = 'thirty_days',
  FortyFiveDays = 'forty_five_days',
  SixtyDays = 'sixty_days',
  NinetyDays = 'ninety_days',
  Prepayment = 'prepayment',
  COD = 'cod'
}

export enum PaymentTermNameEnum {
  ThirtyDays = '30 Days',
  FortyFiveDays = '45 Days',
  SixtyDays = '60 Days',
  NinetyDays = '90 Days',
  Prepayment = 'Prepayment',
  COD = 'Cash on Delivery'
}

export interface CargoGrade {
  created_at?: string;
  // details: null;
  hidden?: boolean;
  hidden_by_default?: boolean;
  id: number;
  is_archived?: boolean;
  name: string;
  stowage_factor_bale_max?: number | null;
  stowage_factor_bale_min?: number | null;
  stowage_factor_grain_max?: number | null;
  stowage_factor_grain_min?: number | null;
  stowage_factor_unit_id?: number | null;
  synonyms?: string;
  type?: string;
  updated_at?: string;
  // updated_by_organization: false;
  vessel_types?: VesselType[];
}

export interface UnitType {
  id: number;
  label: string;
  name: string;
  notation: string;
}

export interface TermType {
  description: string;
  end_day: number;
  start_day: number;
  start_time: string;
  end_time: string;
  id: number;
  multiplier: string;
  name: string;
}

export interface JobExtensionAndDispensationType {
  id: number;
  label: string;
  name: string;
}

export interface CrewTrainingStatusType {
  name?: string;
  value: 'passed' | 'not-passed' | 'retake' | 'training-required' | string;
  sort_index: number;
}

export interface CrewTrainingType {
  id: number;
  name: string;
}

export interface DrillType {
  id: number;
  name: string;
  interval: string;
  january: boolean;
  february: boolean;
  march: boolean;
  april: boolean;
  may: boolean;
  june: boolean;
  july: boolean;
  august: boolean;
  september: boolean;
  october: boolean;
  november: boolean;
  december: boolean;
  sort_index: number;
  is_archived: boolean;
  remarks: string | null;
  created_at: string;
  updated_at: string;
  updated_at_on_sync_to_onboard: string;
  deleted_at: string | null;
}

export interface Airport {
  id: number;
  iata_code: string;
  country_code: string;
  name: string;
}

export enum SurveyDifferenceApplicableType {
  None = 'none',
  Departure = 'departure_report',
  PortNoon = 'port_noon_or_departure_report'
}

export enum BaseCurrencyEquivalentCalculation {
  REGULAR = 'regular',
  INVERSE = 'inverse'
}

export enum InvoicesPrefillPostingDateWith {
  InvoiceDate = 'invoice_date',
  CreationDate = 'creation_date'
}

export enum NegativeReemploymentStatusType {
  SNOOZED = 'snoozed',
  TERMINATED = 'terminated',
  PENDING = 'pending',
  NONE = ''
}

export enum DocumentWarningCountsEnum {
  STATUTORY = 's',
  MANDATORY = 'm',
  RECOMMENDED = 'r'
}

export enum MgaInventoryDepletionMethod {
  NONE = 'none',
  FIFO = 'fifo'
}
